'use client'

import { ColumnDef } from '@tanstack/react-table'
import { LoginAttempt } from '../types'
import { Badge } from '@/components/ui/badge'
import { DataTableColumnHeader } from '@/components/shared/data-table/data-table-column-header'

const getStatusBadgeVariant = (status: string) => {
  switch (status.toLowerCase()) {
    case 'success':
      return 'default'
    case 'failed':
      return 'destructive'
    case 'blocked':
      return 'secondary'
    default:
      return 'outline'
  }
}

const getAttemptTypeBadgeVariant = (type: string) => {
  switch (type.toLowerCase()) {
    case 'login':
      return 'default'
    case 'magic_link':
      return 'secondary'
    case 'password_reset':
      return 'outline'
    default:
      return 'outline'
  }
}

export const columns: ColumnDef<LoginAttempt>[] = [
  {
    accessorKey: 'createdAt',
    enableSorting: true,
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Date" />
    ),
    cell: ({ row }) => {
      const date = new Date(row.getValue('createdAt'))
      return (
        <div className="font-medium">
          {date.toLocaleDateString()} {date.toLocaleTimeString()}
        </div>
      )
    },
  },
  {
    accessorKey: 'attempt_type',
    enableSorting: true,
    header: 'Type',
    cell: ({ row }) => {
      const type = row.getValue('attempt_type') as string
      return (
        <Badge variant={getAttemptTypeBadgeVariant(type)}>
          {type?.replace('_', ' ').toUpperCase() || 'UNKNOWN'}
        </Badge>
      )
    },
  },
  {
    accessorKey: 'status',
    header: 'Status',
    cell: ({ row }) => {
      const status = row.getValue('status') as string
      return (
        <Badge variant={getStatusBadgeVariant(status)}>
          {status?.toUpperCase() || 'UNKNOWN'}
        </Badge>
      )
    },
  },
  {
    accessorKey: 'email',
    header: 'Email',
    cell: ({ row }) => {
      const email = row.getValue('email') as string
      return <div className="font-medium">{email || '-'}</div>
    },
  },
  {
    accessorKey: 'ip_address',
    header: 'IP Address',
    cell: ({ row }) => {
      const ip = row.getValue('ip_address') as string
      return <div className="font-mono text-sm">{ip}</div>
    },
  },
  {
    accessorKey: 'user_agent',
    header: 'User Agent',
    cell: ({ row }) => {
      const userAgent = row.getValue('user_agent') as string
      return (
        <div className="max-w-xs truncate text-sm text-gray-600">
          {userAgent || '-'}
        </div>
      )
    },
  },
  {
    accessorKey: 'failure_reason',
    header: 'Failure Reason',
    cell: ({ row }) => {
      const reason = row.getValue('failure_reason') as string
      return (
        <div className="text-sm">
          {reason ? (
            <Badge variant="outline" className="text-xs">
              {reason.replace('_', ' ')}
            </Badge>
          ) : (
            '-'
          )}
        </div>
      )
    },
  },
  {
    accessorKey: 'customer_id',
    header: 'Customer ID',
    cell: ({ row }) => {
      const customerId = row.getValue('customer_id') as number
      return <div className="text-sm">{customerId || '-'}</div>
    },
  },
]
